import { computed } from "@angular/core";

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from "@ngrx/signals";

import { DEFAULT_LOCALE } from "@/app/i18n.config";
import { Theme } from "@/app/layout/models";
import { LINKS } from "@/app/shared/constants";
import { StorageUtil } from "@/app/shared/utils/storage.util";

const THEME_KEY = "theme";
const FAV_KEY = "nav_fav";

type LayoutState = {
  theme: Theme;
  favUrls: string[];
  locale: Intl.Locale;
};

const initialState: LayoutState = {
  theme: Theme.system,
  favUrls: [],
  locale: DEFAULT_LOCALE,
};

/**
 * Global layout store managing theme, favorites, and locale.
 */
export const LayoutStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed(({ theme }) => ({
    /**
     * Computed theme color reflecting dark or light mode based on current theme.
     */
    themeColor: computed(() => getThemeColor(theme())),
  })),
  withMethods((store) => ({
    /**
     * Set the current theme and apply it.
     *
     * @param {Theme} theme - Theme to set.
     */
    setTheme(theme: Theme): void {
      patchState(store, { theme });
      StorageUtil.set(THEME_KEY, theme);
      applyTheme(store.themeColor());
    },

    /**
     * Add a favorite URL if not already present.
     *
     * @param {string} url - URL to add.
     */
    addFavUrl(url: string): void {
      const favUrls = store.favUrls();
      if (!favUrls.includes(url)) {
        const updatedFavUrls = [...favUrls, url];
        patchState(store, { favUrls: updatedFavUrls });
        saveFavUrls(updatedFavUrls);
      }
    },

    /**
     * Remove a favorite URL.
     *
     * @param {string} url - URL to remove.
     */
    removeFavUrl(url: string): void {
      const updatedFavUrls = store.favUrls().filter((fav) => fav !== url);
      patchState(store, { favUrls: updatedFavUrls });
      saveFavUrls(updatedFavUrls);
    },
  })),
  withHooks({
    /**
     * Initialize store with saved theme and favorites.
     */
    onInit(store) {
      const theme = getTheme();
      applyTheme(getThemeColor(theme));
      patchState(store, { theme, favUrls: getFavUrls() });
    },
  }),
);

// --- Helper functions ---

/**
 * Get saved theme from storage or default to system.
 *
 * @returns {Theme} Current theme.
 */
function getTheme(): Theme {
  const savedTheme = StorageUtil.get<Theme>(THEME_KEY) as Theme;
  return [Theme.light, Theme.dark, Theme.system].includes(savedTheme) ? savedTheme : Theme.system;
}

/**
 * Get saved favorite URLs, validate against available links, and deduplicate.
 *
 * @returns {string[]} List of valid favorite URLs.
 */
function getFavUrls(): string[] {
  // Get saved urls
  const savedUrls: string[] = (() => {
    try {
      return StorageUtil.get<string[]>(FAV_KEY) ?? [];
    } catch {
      return [];
    }
  })();

  // Early return if no url saved
  if (savedUrls.length === 0) {
    return [];
  }

  // Generate a list of all valid url
  const allUrls = LINKS.flatMap((link) =>
    link.items ? link.items.map((item) => item.url) : [link.url],
  ).filter((url) => url !== null && url !== undefined);

  // Filter fav to keep only those that still exist and are unique
  const validUrls = savedUrls.filter((url) => allUrls.includes(url));
  const uniqueUrls = [...new Set(validUrls)];

  // If the lists are different, save the valid one
  if (savedUrls.length !== uniqueUrls.length) {
    saveFavUrls(uniqueUrls);
  }

  return uniqueUrls;
}

/**
 * Resolve the actual theme color (light/dark) based on system preference.
 *
 * @param {Theme} theme - Theme to resolve.
 * @returns {Theme.light | Theme.dark} Actual theme color.
 */
function getThemeColor(theme: Theme): Theme.light | Theme.dark {
  if (theme === Theme.system) {
    const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return isDark ? Theme.dark : Theme.light;
  }
  return theme;
}

/**
 * Apply theme to the document root.
 *
 * @param {Theme} theme - Theme to apply.
 */
function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === Theme.dark);
}

/**
 * Save favorite URLs to storage.
 *
 * @param {string[]} urls - URLs to save.
 */
function saveFavUrls(urls: string[]): void {
  StorageUtil.set(FAV_KEY, urls);
}
